import React, { useMemo, useState, useEffect, useCallback } from "react";
import RemoveIcon from "../../../assets/icons/remove-icon.svg";
import MUIBoxTable from "../../../common/components/mui-table/mui-box-table";
import UserFilter from "./user-filter/user-filter";
import {
  assignDepartmentOwner,
  getDeptartmentOwners,
  getUsersByRoles,
  updateRoles,
} from "../../../api/manage-permissions";
import { getAllEmployees } from "../../../api/dashboard";
import { useSetLoading } from "../../../common/hooks/appearance";
import UserAddPopup from "./user-add-popup/user-add-popup";
import RemoveUserConfirmationPopup from "../../../common/components/delete-confirmation-popup/remove-user-confirmation-popup";
import { User, UserListProps } from "../manage-permission.types";
import "./user-list.scss";

const UserList: React.FC<UserListProps> = ({ headers, activeTab }) => {
  const [loading, setLoading] = React.useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = React.useState("");
  const [isOpenAddPopup, setIsOpenAddPopup] = useState(false);
  const [popupUsers, setPopupUsers] = useState<User[]>([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [isRemovePopupOpen, setIsRemovePopupOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);

  const handleCloseAddPopup = () => {
    setIsOpenAddPopup(false);
    setSelectedDepartmentId(null);
  };

  const addDepartmentOwner = async (employeeData: any) => {
    if (selectedDepartmentId === null) {
      console.error("No department selected");
      return;
    }
    try {
      setLoading(true);
      await assignDepartmentOwner({
        employeeId: employeeData[0],
        departmentId: selectedDepartmentId,
        applicationId: 1,
        roleId: 3,
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Error assigning department owner:", err);
    } finally {
      handleCloseAddPopup();
      fetchUsers();
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let userData: User[] = [];
      if (activeTab === "admins") {
        const res = await getUsersByRoles(1);
        userData = res.data.data;
      } else if (activeTab === "elt") {
        const res = await getUsersByRoles(2);
        userData = res.data.data;
      } else {
        const deptOwners = await getDeptartmentOwners();
        const deptOwnersData = deptOwners.data.data || [];
        userData = deptOwnersData.map((user: any) => {
          return {
            firstName: user.ownerFirstName ?? "-",
            lastName: user.ownerLastName ?? "-",
            email: user.emailId ?? "-",
            ...user,
          };
        });
      }
      const usersWithNames = userData.map((user: User) => {
        const userObject: User = {
          ...user,
        };

        if (activeTab === "elt") {
          userObject.add = "-";
        }
        if (activeTab === "department_owners") {
          const hasRealUser =
            user.firstName &&
            user.firstName !== "-" &&
            user.lastName &&
            user.lastName !== "-" &&
            user.emailId &&
            user.emailId !== "-";
          if (hasRealUser) {
            userObject.add = "-";
          } else {
            userObject.add = (
              <button
                className="user-add-popup-add-button"
                onClick={() => handleOpenAddPopup(user.departmentId)}
              >
                Add User
              </button>
            );
          }
        }
        return userObject;
      });
      setAllUsers(usersWithNames || []);
      setUsers(usersWithNames || []);
    } catch (err: any) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  const fetchDeptOwnersForPopup = async () => {
    setPopupLoading(true);
    try {
      const response = await getAllEmployees({})
        ?.then((data: any) => {
          return data?.data?.data?.map((data: any) => {
            const userName = data?.employeeFullName?.split(" ");
            return {
              email: data?.emailId,
              firstName: userName?.length ? userName[0] : "",
              lastName: userName?.length >= 2 ? userName[1] : "",
              pk: data?.employeeId,
            };
          });
        })
        ?.catch(() => {
          return [];
        });
      setPopupUsers(response);
    } catch (error) {
      console.error("Error fetching users for popup:", error);
    } finally {
      setPopupLoading(false);
    }
  };

  const handleOpenAddPopup = (departmentId: number) => {
    setSelectedDepartmentId(departmentId);
    fetchDeptOwnersForPopup();
    setIsOpenAddPopup(true);
  };

  const handleRemoveClick = useCallback((user: User) => {
    setSelectedUser(user);
    setIsRemovePopupOpen(true);
  }, []);

  const handleCloseRemovePopup = () => {
    setIsRemovePopupOpen(false);
    setSelectedUser(null);
  };

  const handleDelete = async () => {
    if (selectedUser) {
      try {
        setLoading(true);
        await updateRoles({
          employeeId: selectedUser?.pk || selectedUser.employeeId,
          roleName: activeTab === "elt" ? "ELT" : "Department Owner",
          departmentId: selectedUser?.departmentId ?? null,
          applicationId: 1,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error removing user role:", error);
        setLoading(false);
      } finally {
        handleCloseRemovePopup();
        fetchUsers();
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (searchText) {
      const filtered = allUsers.filter((user) => {
        const searchLower = searchText.toLowerCase();
        return (
          user.employeeFullName?.toLowerCase().includes(searchLower) ||
          user.departmentName?.toLowerCase().includes(searchLower) ||
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.emailId?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
        );
      });
      setUsers(filtered);
    } else {
      setUsers(allUsers);
    }
  }, [searchText, allUsers]);

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading List Of Users...";
    }
    if (searchText) {
      return "No users found matching your search";
    }
    return "No users available";
  }, [loading, searchText]);

  const tableColumnsUseMemo = (row: User) => {
    if (row.add === "-") {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveClick(row);
          }}
          style={{
            cursor: "pointer",
            border: "none",
            background: "transparent",
            padding: 0,
          }}
        >
          <img
            src={RemoveIcon}
            alt="Remove"
            className="remove-icon"
            style={{ cursor: "pointer", width: 18, height: 18 }}
          />
        </button>
      );
    }
    return "-";
  };
  const tableColumns = useMemo(() => {
    return headers.map((col) => {
      if (col.key === "remove") {
        return {
          ...col,
          render: (row: User) => tableColumnsUseMemo(row),
        };
      }
      return col;
    });
  }, [headers, handleRemoveClick]);

  const showLoading = useMemo(
    () => loading || popupLoading,
    [loading, popupLoading],
  );
  useSetLoading(showLoading);

  return (
    <>
      <div className="user-list-table-container">
        <UserFilter
          searchText={searchText}
          onSearch={setSearchText}
          activeTab={activeTab}
          fetchUsers={fetchUsers}
          users={users}
        />
        <MUIBoxTable
          columns={tableColumns}
          data={users}
          noResultText={noResultText}
          onRemoveClick={handleRemoveClick}
        />
      </div>
      {isOpenAddPopup ? (
        <UserAddPopup
          isOpen={isOpenAddPopup}
          onClose={handleCloseAddPopup}
          activeTab={activeTab}
          users={popupUsers}
          loading={popupLoading}
          onAddUsers={addDepartmentOwner}
        />
      ) : (
        ""
      )}
      {selectedUser && (
        <RemoveUserConfirmationPopup
          isOpen={isRemovePopupOpen}
          onClose={handleCloseRemovePopup}
          handleDelete={handleDelete}
          deleteId={selectedUser.employeeId}
          label={"elt" === activeTab ? "ELT" : "Department"}
        />
      )}
    </>
  );
};

export default UserList;
